import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { SignupStateService } from '../../services/signup-state.service';
import { PASOS_EMPRESA } from '../../constants/signup.constants';
import {
  KycApiService,
  ErutAnalysisResponse,
} from 'src/app/auth/services/kyc-api.service';
import { CompanyRegistrationService } from 'src/app/auth/services/company-registration.service';

/** Limite del backend (FileValidationPipe: fileUpload.maxFileSizeMB) */
const MAX_FILE_SIZE_MB = 10;

type ErutStatus =
  | 'idle'
  | 'selected'
  | 'analyzing'
  | 'accepted'
  | 'review'
  | 'rejected'
  | 'duplicada';

@Component({
  selector: 'app-erut-upload-step',
  templateUrl: './erut-upload-step.component.html',
  styleUrls: ['./erut-upload-step.component.sass'],
})
export class ErutUploadStepComponent implements OnInit {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  status: ErutStatus = 'idle';
  selectedFile: File | null = null;
  analysis: ErutAnalysisResponse | null = null;

  constructor(
    public signupState: SignupStateService,
    private kycService: KycApiService,
    private companyService: CompanyRegistrationService
  ) {}

  ngOnInit(): void {
    // El unico prerrequisito es tener el usuario de Cognito: de ahi sale el
    // `cognitoId` con el que se archiva el PDF. La verificacion de identidad ya
    // no es requisito — este paso va antes, justamente para no pedirle camara y
    // selfie a una empresa que despues resulta estar registrada.
    if (!this.signupState.currentState.cognitoId) {
      this.onError.emit('Primero verifica tu correo.');
      this.signupState.goToStep(PASOS_EMPRESA.OTP_EMAIL);
      return;
    }

    // Restaurar el resultado si el usuario ya cargo el e-RUT y volvio atras
    const saved = this.signupState.erutAnalysis;
    if (saved) {
      this.status = saved.needsManualReview ? 'review' : 'accepted';
      this.analysis = {
        recommendation: saved.recommendation,
        integrityScore: saved.integrityScore,
        checks: [],
        reasons: [],
        notes: [],
        fields: {
          rut: saved.rut,
          razonSocial: saved.razonSocial,
          direccion: saved.direccion,
          serie: saved.serie,
          glosaActividad: saved.glosaActividad,
          fechaEmision: saved.fechaEmision,
          rutUsuario: saved.rutUsuario,
          usuario: saved.usuario,
        },
        crossCheck: { allMatch: true, mismatchedFields: [], unprotectedFields: [] },
        rutCheckDigitValid: true,
        sha256: saved.sha256,
        verifiedWithSii: false,
      };
    }
  }

  // ==================== SELECCION DE ARCHIVO ====================

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    // Limpiar el input para que seleccionar el mismo archivo vuelva a disparar el evento
    input.value = '';

    if (!file) return;

    const error = this.validateFile(file);
    if (error) {
      this.onError.emit(error);
      return;
    }

    this.selectedFile = file;
    this.analysis = null;
    this.status = 'selected';
  }

  private validateFile(file: File): string | null {
    const isPdf =
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      return 'El e-RUT debe ser el PDF que descargas del SII, no una foto ni una captura de pantalla.';
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      return `El archivo pesa ${sizeMB.toFixed(1)} MB y el maximo es ${MAX_FILE_SIZE_MB} MB.`;
    }

    if (file.size === 0) {
      return 'El archivo esta vacio. Descarga el e-RUT nuevamente desde el SII.';
    }

    return null;
  }

  clearFile(): void {
    this.selectedFile = null;
    this.analysis = null;
    this.status = 'idle';
  }

  get fileSizeLabel(): string {
    if (!this.selectedFile) return '';
    const kb = this.selectedFile.size / 1024;
    return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  }

  // ==================== ANALISIS ====================

  async analyze(): Promise<void> {
    if (!this.selectedFile || this.status === 'analyzing') return;

    this.status = 'analyzing';

    try {
      const result = await this.kycService.analyzeErut(this.selectedFile);
      this.analysis = result;

      // El RUT de la sociedad recien se conoce aca, cuando el e-RUT se analiza:
      // el registro vacio del OTP de WhatsApp se crea sin el. Es el unico punto
      // del flujo donde se puede cortar una empresa que ya tiene cuenta, y por
      // eso esta pantalla va primera.
      if (await this.rutYaRegistrado(result.fields?.rut)) {
        return;
      }

      await this.processResult(result);
    } catch (error: any) {
      this.status = 'selected';
      this.onError.emit(error?.message || 'Error analizando el e-RUT. Intenta nuevamente.');
    }
  }

  /**
   * Corta si la empresa del e-RUT ya esta registrada.
   *
   * Sin esto dos socios de la misma sociedad pueden registrarla dos veces: cada
   * uno pasa con su propia cedula y su propio correo, ninguno choca con el
   * chequeo del documento del representante legal, y quedan dos empresas con el
   * mismo RUT — y con dos cupos de carga.
   *
   * Una falla de red NO bloquea: se deja avanzar y el duplicado lo caza la
   * consolidacion. Es el mismo criterio que usa el chequeo de documento de
   * persona: no dejar a un cliente legitimo afuera por un timeout.
   */
  private async rutYaRegistrado(rut?: string | null): Promise<boolean> {
    if (!rut) return false;

    try {
      const check = await this.companyService.checkCompanyRut(rut);
      if (!check?.isRegistered) return false;

      // El back ya no dice de quién es el RUT (R5): antes se mostraba "como
      // pagador" y se mandaba a iniciar sesión, aunque un pagador no tiene cuenta.
      this.status = 'duplicada';
      this.signupState.setErutAnalysis(null);
      return true;
    } catch (error: any) {
      return false;
    }
  }

  private async processResult(result: ErutAnalysisResponse): Promise<void> {
    if (result.recommendation === 'reject') {
      // Evidencia de manipulacion: no se guarda nada, hay que subir otro documento
      this.status = 'rejected';
      this.signupState.setErutAnalysis(null);
      return;
    }

    // El PDF se guarda ANTES del estado, y si falla no se guarda el veredicto:
    // asi se mantiene la invariante de que un e-RUT aceptado siempre tiene su
    // documento archivado. Un dictamen sin documento no se puede auditar ni
    // volver a revisar, y es justo lo que compliance necesita ver.
    const s3Key = await this.guardarPdf();
    if (!s3Key) {
      this.status = 'selected';
      this.onError.emit(
        'No pudimos guardar el e-RUT. Reintenta en unos segundos.'
      );
      return;
    }

    const needsManualReview = result.recommendation === 'review';

    this.signupState.setErutAnalysis({
      recommendation: result.recommendation,
      integrityScore: result.integrityScore,
      needsManualReview,
      sha256: result.sha256,
      s3Key,
      rut: result.fields.rut,
      razonSocial: result.fields.razonSocial,
      direccion: result.fields.direccion,
      serie: result.fields.serie,
      glosaActividad: result.fields.glosaActividad,
      fechaEmision: result.fields.fechaEmision,
      // Se cruzan contra el representante legal en el paso siguiente
      rutUsuario: result.fields.rutUsuario,
      usuario: result.fields.usuario,
    });

    this.status = needsManualReview ? 'review' : 'accepted';
    this.onSuccess.emit(
      needsManualReview
        ? 'e-RUT recibido: quedara en revision manual'
        : 'e-RUT verificado correctamente'
    );
  }

  /**
   * Sube el PDF del e-RUT a S3 y devuelve su clave, o `null` si no se pudo.
   *
   * Va con `duranteRegistro` en true para que un 401 no se trate como sesion
   * expirada y expulse al usuario al login a mitad del alta. El endpoint de
   * almacenamiento acepta PDF: detecta la cabecera `%PDF`, valida el numero
   * magico y escanea inyeccion con una rama propia para PDF.
   */
  private async guardarPdf(): Promise<string | null> {
    if (!this.selectedFile) return null;

    const cognitoId = this.signupState.currentState.cognitoId;
    if (!cognitoId) return null;

    try {
      const base64 = await this.aBase64(this.selectedFile);
      const response = await this.companyService.sendFile(
        { name: 'erut_empresa', base64, id: cognitoId },
        true
      );
      return response?.key || null;
    } catch (error: any) {
      return null;
    }
  }

  private aBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /** Motivos a mostrar cuando se rechaza. El ultimo es la nota fija sobre la firma. */
  get rejectionReasons(): string[] {
    const reasons = this.analysis?.reasons || [];
    return reasons.filter((r) => !r.includes('no lleva firma criptografica'));
  }

  // ==================== NAVEGACION ====================

  async continue(): Promise<void> {
    if (this.status !== 'accepted' && this.status !== 'review') return;
    await this.signupState.nextStep();
  }

  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }
}
